/**
 * Shared version-settings property editors for classic playables.
 * Used by Version Settings TreeView and Scene Editor inspector.
 *
 * Optional `wrapSetting(editorNode, setting, { component, index })` lets callers
 * wrap each row (e.g. scene FieldRow) while still mapping settings in one pass.
 */

import React, { useState } from "react";
import _ from "lodash";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Tooltip from "@material-ui/core/Tooltip";
import { Button, Dialog, DialogTitle } from "@mui/material";
import { FileCopy } from "@mui/icons-material";
import EditorText from "./editorText";
import EditorTextArea from "./editorTextArea";
import EditorGridArea from "./editorGridArea";
import SelectorColor from "./selectorColor";
import SelectorImageSet from "./selectorImageSet";
import SelectorTexture from "./selectorTexture";
import SelectorImage from "./selectorImage";
import SelectorAtlas from "./selectorAtlas";
import SelectorSpine from "./selectorSpine";
import SelectorSound from "./selectorSound";
import SelectorGlb from "./selectorGlb";
import SelectorVideo from "./selectorVideo";
import SelectorLevel from "./selectorLevel";
import SelectorAnimation from "./selectorAnimation";
import SwitchGeneric from "./switchGeneric";
import SliderGeneric from "./sliderGeneric";
import SelectorGeneric from "./selectorGeneric";
import SelectorCardOrder from "./selectorCardOrder";
import SelectorFontFamily from "./selectorFontFamily";
import SelectorFontStyle from "./selectorFontStyle";
import SelectorFont from "./selectorFont";
import SelectorRibbonType from "./selectorRibbonType";
import SelectorLevelType from "./selectorLevelType";
import CardItemsSelector from "./cardItemsSelector";
import EditorNumber from "./editorNumber";
import EditorObjectMap from "./editorObjectMap";
import { pushPreviewPatch, buildComponentPatch } from "../modules/previewPatch";

function CopyToAllLevelsButton({ setting, componentId, selectedVersion, setSelectedVersionPathValue, userPermissions, createSnackbar }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const selectedLevel = _.get(selectedVersion, `gameConfig.components.${componentId}.${setting.path}`, _.get(setting, "items[0]", "Level 1"));

  const handleClick = () => {
    if (userPermissions["editContent"] === false) {
      createSnackbar("You do not have permission to update, contact your administrator.", "warning", false);
      return;
    }
    setIsDialogOpen(true);
  };

  const executeCopy = () => {
    setIsDialogOpen(false);
    const sourceLevel = selectedLevel.replace(/\s/g, "").toLowerCase();
    const allComponents = _.cloneDeep(_.get(selectedVersion, "gameConfig.components", {}));

    let copyCount = 0;

    // Copy ALL components that have levelConfigs with the source level
    Object.keys(allComponents).forEach((compId) => {
      if (_.has(allComponents[compId], `levelConfigs.${sourceLevel}`)) {
        const sourceData = allComponents[compId].levelConfigs[sourceLevel];
        setting.items.forEach((item) => {
          const targetLevel = item.replace(/\s/g, "").toLowerCase();
          if (targetLevel !== sourceLevel) {
            allComponents[compId].levelConfigs[targetLevel] = _.cloneDeep(sourceData);
          }
        });
        copyCount++;
      }
    });

    if (copyCount === 0) {
      createSnackbar("No level configurations found to copy.", "warning", false);
      return;
    }

    setSelectedVersionPathValue({
      path: "gameConfig.components",
      value: allComponents,
    });

    createSnackbar(
      `${selectedLevel} values copied to all other levels successfully.`,
      "success",
      false
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileCopy />}
        onClick={handleClick}
        style={{
          marginBottom: 10,
          textTransform: "none",
          color: "#9bc3ce",
          borderColor: "#9bc3ce",
        }}
      >
        {`Copy ${selectedLevel} Configs to All levels`}
      </Button>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: "#2a2a2a",
            borderRadius: 12,
            padding: "8px 12px",
            minWidth: 320,
          },
        }}
      >
        <DialogTitle style={{ color: "#e0e0e0", fontSize: "1rem", padding: "12px 16px 4px" }}>
          {`Copy ${selectedLevel} to all other levels?`}
        </DialogTitle>
        <p style={{ color: "#999", fontSize: "0.8rem", margin: "0 16px 16px", lineHeight: 1.4 }}>
          This will overwrite level configurations for all other levels.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "0 16px 12px" }}>
          <Button
            onClick={() => setIsDialogOpen(false)}
            style={{
              textTransform: "none",
              color: "#999",
              borderColor: "#555",
              borderRadius: 8,
            }}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={executeCopy}
            style={{
              textTransform: "none",
              backgroundColor: "#50ADB6",
              color: "#fff",
              borderRadius: 8,
            }}
            variant="contained"
            size="small"
          >
            Copy
          </Button>
        </div>
      </Dialog>
    </>
  );
}


export function getTextWidth(ttt) {
  let text = document.createElement("span");
  document.body.appendChild(text);
  text.style.font = "Roboto, Helvetica, Arial, sans-serif";
  text.style.fontSize = "0.9285714285714286rem";
  text.style.height = "auto";
  text.style.width = "auto";
  text.style.letterSpacing = "0.00938em";
  text.style.position = "absolute";
  text.style.whiteSpace = "no-wrap";
  text.innerHTML = ttt;
  let width = Math.ceil(text.clientWidth);
  document.body.removeChild(text);
  return width;
}

export function renderTooltip(tooltip, isVisible, marginLeft) {
  return isVisible && tooltip ? (
    <div>
      <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>} aria-label="add">
        <HelpOutlineIcon style={{ marginLeft: marginLeft }} color={"primary"} />
      </Tooltip>
    </div>
  ) : null;
}

// Aliased so the component below can shadow `renderTooltip` with a no-op without
// tripping over the temporal dead zone of its own const.
const renderTooltipInline = renderTooltip;


/**
 * Renders all editor rows for one versionSettings component.
 */
export default function VersionComponentSettingsEditors({
  component,
  props,
  classes,
  setValue,
  realSelectedVersion,
  wrapSetting,
  hideInlineTooltips = false,
}) {
  // The Scene Editor wraps every classic editor in its own FieldRow, which already
  // draws the tooltip next to the label. Leaving the editor's own icon in place
  // showed two help markers on the same row, in two different themes.
  const renderTooltip = hideInlineTooltips ? () => null : renderTooltipInline;
  return (
    <>
{(() => {
  const copyToAllSetting = _.find(component.settings, (s) => s.editorType === "selectorLevelType" && s.copyToAllLevels);
  if (copyToAllSetting) {
    return (
      <CopyToAllLevelsButton
        setting={copyToAllSetting}
        componentId={component.id}
        selectedVersion={realSelectedVersion}
        setSelectedVersionPathValue={props.setSelectedVersionPathValue}
        userPermissions={props.userPermissions}
        createSnackbar={props.createSnackbar}
      />
    );
  }
  return null;
})()}
{/* eslint-disable-next-line no-unused-vars */}
{_.get(component, "settings", []).map((setting, index) => {
  const rendered = (() => {
  if (setting.visibleWhen) {
    let visibleValue = _.get(
      props,
      `selectedVersion.gameConfig.components.${component.id}.${setting.visibleWhen.path}`
    );
    if (visibleValue == null || visibleValue === "") {
      visibleValue = setting.visibleWhen.default;
    }
    if (String(visibleValue) !== String(setting.visibleWhen.equals)) {
      return null;
    }
  }

  const isVisible = true;
  let sliderGenericPath = `gameConfig.components.${component.id}.${setting.path}`;
  let sliderGenericValue = _.get(
    props,
    `selectedVersion.gameConfig.components.${component.id}.${setting.path}`,
    {}
  );

  if (setting.editorSubType) {
    sliderGenericPath = setting.editorSubType.includes("FullPath")
      ? `gameConfig.components.${setting.path}`
      : `gameConfig.components.${component.id}.${setting.path}`;

    sliderGenericValue = setting.editorSubType.includes("FullPath")
      ? _.get(props, `selectedVersion.gameConfig.components.${setting.path}`, {})
      : _.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, {});
  }

  if (setting.editorType === "editorCardOrder") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorCardOrder
          id={`${component.id}.${setting.path}`}
          key={`${component.id}.${setting.path}`}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          cardOrders={_.get(props, "selectedBase.cardOrders")}
          componentID={component.id}
          classes={props.classes}
          selectedCardOrderKey={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.cardOrder.key`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        />
      </div>
    );
  }
  if (setting.editorType === "editorText") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <EditorText
          id={`${component.id}.${setting.path}`}
          componentID={component.id}
          classes={props.classes}
          tooltipLength={-24}
          tooltip={setting.tooltip}
          caption={setting.caption}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          livePreview={(value) => {
            const patch = buildComponentPatch(
              props.selectedVersion,
              `gameConfig.components.${component.id}.${setting.path}`,
              value
            );
            if (patch) pushPreviewPatch(patch);
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        />
      </div>
    );
  }
  if (setting.editorType === "editorTextArea") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <EditorTextArea
          id={`${component.id}.${setting.path}`}
          storageKey={`${_.get(props, "selectedBase._id", "")}.${_.get(props, "selectedVersion._id", "")}.${component.id}.${setting.path}`}
          componentID={component.id}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          classes={props.classes}
          caption={setting.caption}
          isDialogEditEnabled={
            _.get(component, "type") === "MatchThreeGrid" ||
            _.get(component, "type") === "sortOutGrid" ||
            _.get(component, "type") === "tilemap" ||
            _.get(component, "type") === "level" ||
            setting.caption === "Level"
          }
          isTextFieldVisible={
            _.get(component, "type") !== "MatchThreeGrid" &&
            _.get(component, "type") !== "tilemap" &&
            _.get(component, "type") !== "level" &&
            setting.caption !== "Level"
          }
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          livePreview={(value) => {
            const patch = buildComponentPatch(
              props.selectedVersion,
              `gameConfig.components.${component.id}.${setting.path}`,
              value
            );
            if (patch) pushPreviewPatch(patch);
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        />
      </div>
    );
  }
  if (setting.editorType === "editorGridArea") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <EditorGridArea
          id={`${component.id}.${setting.path}`}
          storageKey={`${_.get(props, "selectedBase._id", "")}.${_.get(props, "selectedVersion._id", "")}.${component.id}.${setting.path}`}
          componentID={component.id}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          classes={props.classes}
          caption={setting.caption}
          buttonText={setting.caption}
          isDialogEditEnabled={true}
          isTextFieldVisible={false}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          livePreview={(value) => {
            const patch = buildComponentPatch(
              props.selectedVersion,
              `gameConfig.components.${component.id}.${setting.path}`,
              value
            );
            if (patch) pushPreviewPatch(patch);
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        />
      </div>
    );
  }
  if (setting.editorType === "selectorGlb") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}
        <SelectorGlb
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          glbs={_.get(props, "glbs", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          bucket={setting.bucket}
          maxSizeMb={setting.maxSizeMb}
          selectedGlb={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}[0]`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          insertGlbPathValue={props.insertGlbPathValue}
          deleteGlbFromGlbs={props.deleteGlbFromGlbs}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
        ></SelectorGlb>
      </div>
    );
  }
  if (setting.editorType === "selectorVideo") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorVideo
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          label={setting.caption}
          videos={_.get(props, "videos", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          bucket={setting.bucket}
          selectedVideo={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}[0]`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          insertVideoPathValue={props.insertVideoPathValue}
          deleteVideoFromVideos={props.deleteVideoFromVideos}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        />
      </div>
    );
  }
  if (setting.editorType === "selectorImage") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}
        <SelectorImage
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          label={setting.caption}
          classes={props.classes}
          images={_.get(props, "media.images", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          bucket={setting.bucket}
          selectedImage={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}[0]`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          insertMediaPathValue={props.insertMediaPathValue}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          deleteImageFromMedia={props.deleteImageFromMedia}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        ></SelectorImage>
      </div>
    );
  }
  if (setting.editorType === "selectorImageFullPath") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorImage
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          selectedGame={props.selectedGame}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          images={_.get(props, "media.images", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          bucket={setting.bucket}
          selectedImage={_.get(props, `selectedVersion.gameConfig.components.${setting.path}[0]`)}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${setting.path}`,
              value: p.value
            });
          }}
          insertMediaPathValue={props.insertMediaPathValue}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          deleteImageFromMedia={props.deleteImageFromMedia}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        ></SelectorImage>
      </div>
    );
  }
  if (setting.editorType === "selectorSound") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorSound
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          sounds={_.get(props, "sounds", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          bucket={setting.bucket}
          allowZip={setting.allowZip}
          selectedSound={_.get(props, `selectedVersion.gameConfig.components.${setting.path}[0]`)}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${setting.path}`,
              value: p.value
            });
          }}
          insertSoundPathValue={props.insertSoundPathValue}
          deleteSoundFromSounds={props.deleteSoundFromSounds}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
        ></SelectorSound>
      </div>
    );
  }
  if (setting.editorType === "selectorFont") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorFont
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          fonts={_.get(props, "fonts", [])}
          componentType={component.type}
          path={setting.path}
          destination={setting.destination}
          selectedFont={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}`
          )}
          setValue={setValue}
          insertFontPathValue={props.insertFontPathValue}
          deleteFontFromFonts={props.deleteFontFromFonts}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
        ></SelectorFont>
      </div>
    );
  }
  if (setting.editorType === "selectorAtlas") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorAtlas
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          atlases={_.get(props, "atlases", [])}
          componentType={component.type}
          path={setting.path}
          bucket={setting.bucket}
          destinations={setting.destinations}
          selectedAnimationSetID={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}.[0].animationSet`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          insertAtlasPathValue={props.insertAtlasPathValue}
          deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
        ></SelectorAtlas>
      </div>
    );
  }
  if (setting.editorType === "selectorSpine") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorSpine
          id={`${component.id}.${setting.path}`}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          spines={_.get(props, "spines", [])}
          componentType={component.type}
          path={setting.path}
          bucket={setting.bucket}
          destinations={setting.destinations}
          selectedAnimationSetID={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}.[0].animationSet`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value
            });
          }}
          insertSpinePathValue={props.insertSpinePathValue}
          deleteAnimationSetFromSpines={props.deleteAnimationSetFromSpines}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
        ></SelectorSpine>
      </div>
    );
  }
  if (setting.editorType === "selectorTexture") {
    return (
      <div key={"texture"}>
        {_.get(props, `selectedVersion.gameConfig.components.${component.id}.useTexture`) && (
          <SelectorTexture
            id={`${component.id}.${setting.path}`}
            selectedBase={props.selectedBase}
            componentID={component.id}
            classes={props.classes}
            images={_.get(props, "media.images", [])}
            componentType={component.type}
            path={setting.path}
            destination={setting.destination}
            bucket={setting.bucket}
            selectedImage={_.get(
              props,
              `selectedVersion.gameConfig.components.${component.id}.${setting.path}[0]`
            )}
            setValue={(p) => {
              setValue({
                path: `gameConfig.components.${component.id}.${setting.path}`,
                value: p.value
              });
            }}
            insertMediaPathValue={props.insertMediaPathValue}
            userPermissions={props.userPermissions}
            createSnackbar={props.createSnackbar}
            isVisible={isVisible}
          ></SelectorTexture>
        )}
      </div>
    );
  }
  if (setting.editorType === "selectorImageSet") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorImageSet
          id={`${component.id}.${setting.path}`}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          selectedBase={props.selectedBase}
          componentID={component.id}
          label={setting.caption}
          classes={props.classes}
          images={_.get(props, "media.images", [])}
          componentType={component.type}
          path={setting.path}
          bucket={setting.bucket}
          destinations={setting.destinations}
          singleUpload={setting.singleUpload}
          selectedImageSetID={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}.[0].imageSet`
          )}
          setValue={(p) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: p.value //images of the imageSet, in the form of image records
            });
          }}
          insertMediaPathValue={props.insertMediaPathValue}
          createSnackbar={props.createSnackbar}
          selectedVersion={props.selectedVersion}
          deleteImageSetFromMedia={props.deleteImageSetFromMedia}
          userPermissions={props.userPermissions}
          isVisible={isVisible}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        ></SelectorImageSet>
      </div>
    );
  }
  if (setting.editorType === "sliderGeneric") {
    let tooltipLength = getTextWidth(setting.caption);
    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}
        <SliderGeneric
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          tooltipLength={-28}
          newSetPortrait={props.newSetPortrait}
          tooltip={setting.tooltip}
          min={setting.min}
          max={setting.max}
          step={setting.step}
          value={sliderGenericValue}
          path={sliderGenericPath}
          y_axis={!!setting.y_axis}
          editorSubType={setting.editorSubType}
          label={setting.caption}
          setValue={(pathToSet, value) => {
            setValue({
              path: pathToSet,
              value
            });
          }}
          livePreview={(pathToSet, value) => {
            // Drag-time: patch only the playable (no React/persist);
            // the full commit runs once on release via setValue.
            const patch = buildComponentPatch(props.selectedVersion, pathToSet, value);
            if (patch) pushPreviewPatch(patch);
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SliderGeneric>
      </div>
    );
  }
  if (setting.editorType === "switchGeneric") {
    let tooltipLength = getTextWidth(setting.caption);
    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 52)}

        <SwitchGeneric
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          tooltipLength={-54}
          tooltip={setting.tooltip}
          value={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}`,
            false
          )}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SwitchGeneric>
      </div>
    );
  }
  if (setting.editorType === "switchGenericFullPath") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SwitchGeneric
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          tooltipLength={-30}
          value={_.get(props, `selectedVersion.gameConfig.components.${setting.path}`, false)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SwitchGeneric>
      </div>
    );
  }
  if (setting.editorType === "color") {
    let tooltipLength = getTextWidth(setting.caption);
    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorColor
          id={`${component.id}.${setting.path}`}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          value={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.${setting.path}`,
            "#ffffff"
          )}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorColor>
      </div>
    );
  }
  if (setting.editorType === "selectorLevel") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorLevel
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          label={setting.caption}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 1)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorLevel>
      </div>
    );
  }
  if (setting.editorType === "selectorAnimation") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorAnimation
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          label={setting.caption}
          tooltipLength={-28.5}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 1)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorAnimation>
      </div>
    );
  }
  if (setting.editorType === "selectorFontFamily") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorFontFamily
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          label={setting.caption}
          tooltipLength={-24.5}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 1)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          selectedVersion={props.selectedVersion}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorFontFamily>
      </div>
    );
  }
  if (setting.editorType === "selectorFontStyle") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorFontStyle
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          tooltipLength={-26}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 1)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorFontStyle>
      </div>
    );
  }
  if (setting.editorType === "selectorColor") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorColor
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          mode={"String"}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 1)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorColor>
      </div>
    );
  }
  if (setting.editorType === "selectorString") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorGeneric
          id={`${component.id}.${setting.path}`}
          label={setting.caption}
          classes={props.classes}
          tooltipLength={-32.5}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 0)}
          items={setting.items}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></SelectorGeneric>
      </div>
    );
  }
  if (setting.editorType === "selectorRibbonType") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorRibbonType
          id={`${component.id}.${setting.path}`}
          label={setting.caption}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          newSetPortrait={props.newSetPortrait}
          classes={props.classes}
          path={`gameConfig.components.${component.id}.${setting.path}`}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, 0)}
          items={setting.items}
          components={setting.components}
          atlases={_.get(props, "atlases", [])}
          images={_.get(props, "media.images", [])}
          setValue={(pathToSet, value) => {
            setValue({
              path: pathToSet,
              value
            });
          }}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          selectedBase={props.selectedBase}
          selectedVersion={props.selectedVersion}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          isVisible={true}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        ></SelectorRibbonType>
      </div>
    );
  }
  if (setting.editorType === "selectorLevelType") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`} style={{ marginTop: setting.linkedLevelPath ? "0%" : "5%" }}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <SelectorLevelType
          id={`${component.id}.${setting.path}`}
          label={setting.caption}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          newSetPortrait={props.newSetPortrait}
          classes={props.classes}
          path={`gameConfig.components.${component.id}.${setting.path}`}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, "Level 1")}
          items={setting.items}
          components={setting.components}
          linkedLevelPath={setting.linkedLevelPath}
          atlases={_.get(props, "atlases", [])}
          images={_.get(props, "media.images", [])}
          setValue={(pathToSet, value) => {
            setValue({
              path: pathToSet,
              value
            });
          }}
          insertAtlasPathValue={props.insertAtlasPathValue}
          insertMediaPathValue={props.insertMediaPathValue}
          deleteImageFromMedia={props.deleteImageFromMedia}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          selectedBase={props.selectedBase}
          selectedVersion={props.selectedVersion}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          isVisible={true}
          setIsDialogConfirmRefreshOpen={props.setIsDialogConfirmRefreshOpen}
          setIsDialogCircularProgressOpen={props.setIsDialogCircularProgressOpen}
        ></SelectorLevelType>
      </div>
    );
  }
  if (setting.editorType === "cardItemsSelector") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <CardItemsSelector
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`)}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          duration={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.selectedVideos.video[0].duration`
          )}
        ></CardItemsSelector>
      </div>
    );
  }
  if (setting.editorType === "editorObjectMap") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <EditorObjectMap
          id={`${component.id}.${setting.path}`}
          componentID={component.id}
          classes={props.classes}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`, {})}
          setValue={(newMap) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value: newMap
            });
          }}
          livePreview={(pathToSet, value) => {
            const patch = buildComponentPatch(props.selectedVersion, pathToSet, value);
            if (patch) pushPreviewPatch(patch);
          }}
          setting={setting}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
        ></EditorObjectMap>
      </div>
    );
  }
  if (setting.editorType === "number") {
    let tooltipLength = getTextWidth(setting.caption);

    return (
      <div key={`${component.id}.${setting.path}`}>
        {renderTooltip(setting.tooltip, isVisible, tooltipLength + 5)}

        <EditorNumber
          id={`${component.id}.${setting.path}`}
          classes={props.classes}
          tooltipLength={-28}
          tooltip={setting.tooltip}
          value={_.get(props, `selectedVersion.gameConfig.components.${component.id}.${setting.path}`)}
          min={setting.min}
          max={setting.max}
          setValue={(value) => {
            setValue({
              path: `gameConfig.components.${component.id}.${setting.path}`,
              value
            });
          }}
          label={setting.caption}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          isVisible={isVisible}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          duration={_.get(
            props,
            `selectedVersion.gameConfig.components.${component.id}.selectedVideos.video[0].duration`
          )}
        ></EditorNumber>
      </div>
    );
  }
    return null;
  })();
  if (rendered == null) return null;
  if (typeof wrapSetting === "function") {
    return wrapSetting(rendered, setting, { component, index });
  }
  return rendered;
})}

    </>
  );
}
